import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import useDetectCurrentCulture from "./useDetectCurrentCulture";
import useLocalStorage from "./useLocalStorage";
import { useWindow } from "./useWindow";
const useDateTimeFormats = () => {
    const baseOffsets = ["UTC", "Coordinated Universal Time"];
    const { getItem } = useLocalStorage();
    const { culture, defaultCulture } = useDetectCurrentCulture();
    const myWindow = useWindow();
    const [storedFormats, setStoredFormats] = useState();
    const handleDateTimeFormats = () => {
        const localizations = getItem("localizations");
        localizations && setStoredFormats(localizations === null || localizations === void 0 ? void 0 : localizations.dateTimeFormats);
    };
    const dateTimeFormats = useMemo(() => {
        var _a, _b, _c, _d, _e, _f;
        const date = (_b = (_a = storedFormats === null || storedFormats === void 0 ? void 0 : storedFormats.dateFormatByCulture[culture]) !== null && _a !== void 0 ? _a : storedFormats === null || storedFormats === void 0 ? void 0 : storedFormats.dateFormatByCulture[defaultCulture]) !== null && _b !== void 0 ? _b : null;
        const time = (_d = (_c = storedFormats === null || storedFormats === void 0 ? void 0 : storedFormats.timeFormatByCulture[culture]) !== null && _c !== void 0 ? _c : storedFormats === null || storedFormats === void 0 ? void 0 : storedFormats.timeFormatByCulture[defaultCulture]) !== null && _d !== void 0 ? _d : null;
        const dateTime = (_f = (_e = storedFormats === null || storedFormats === void 0 ? void 0 : storedFormats.dateAndTimeFormatByCulture[culture]) !== null && _e !== void 0 ? _e : storedFormats === null || storedFormats === void 0 ? void 0 : storedFormats.dateAndTimeFormatByCulture[defaultCulture]) !== null && _f !== void 0 ? _f : null;
        return {
            date,
            time,
            dateTime
        };
    }, [storedFormats, culture]);
    const hasOffsetInFormat = useCallback(() => {
        // set offset to UTC for reset
        const baseFormattedDate = DateTime.fromMillis(0).setLocale("en-GB");
        const resetDate = baseFormattedDate.setZone("Etc/UTC");
        // check date and time formats if using the format produces an offset
        const hasOffsetInDate = baseOffsets.some((offsetOutput) => typeof dateTimeFormats.date === "string" && resetDate.toFormat(dateTimeFormats.date).includes(offsetOutput));
        const hasOffsetInTime = baseOffsets.some((offsetOutput) => typeof dateTimeFormats.time === "string" && resetDate.toFormat(dateTimeFormats.time).includes(offsetOutput));
        return hasOffsetInDate || hasOffsetInTime;
    }, [dateTimeFormats]);
    const getLocalDateTime = (dateTime) => {
        const timeZone = DateTime.fromISO(dateTime).zone.name;
        return DateTime.fromISO(dateTime).setLocale(culture).setZone(timeZone);
    };
    const getSplittedDayTime = (dateTime) => {
        const date = getLocalDateTime(dateTime);
        const day = date.toLocaleString({ day: "numeric" });
        const month = date.toLocaleString({ month: "short" });
        const year = date.toLocaleString({ year: "numeric" });
        return {
            day,
            month,
            year
        };
    };
    const formatTime = (rawTime) => {
        const time = getLocalDateTime(rawTime);
        if (typeof dateTimeFormats.time === "string") {
            return time.toFormat(dateTimeFormats.time);
        }
        else {
            return time.toFormat("HH:mm");
        }
    };
    const formatDate = (rawDate) => {
        const date = getLocalDateTime(rawDate);
        if (typeof dateTimeFormats.date === "string") {
            return date.toFormat(dateTimeFormats.date);
        }
        else {
            return date.toLocaleString(DateTime.DATE_FULL);
        }
    };
    useEffect(() => {
        if (!!culture && myWindow) {
            handleDateTimeFormats();
        }
    }, [myWindow && culture]);
    useEffect(() => {
        const sub = myWindow && myWindow.addEventListener("storage", handleDateTimeFormats);
        return () => sub;
    }, [myWindow]);
    return {
        getSplittedDayTime,
        dateTimeFormats,
        hasOffsetInFormat,
        formatTime,
        formatDate
    };
};
export default useDateTimeFormats;
